<template>
    <div class="profile-card">
        <div class="card-header">
            <h2>Personal Information</h2>
            <button
                v-if="!editing"
                @click="startEditing"
                class="edit-btn"
            >
                Edit
            </button>
        </div>
        <div class="card-content">
            <!-- Full Name -->
            <div class="info-row">
                <label>Full Name</label>
                <input
                    v-if="editing"
                    v-model="localFormData.full_name"
                    type="text"
                    class="input-field"
                    placeholder="Enter your full name"
                />
                <span v-else class="info-value">{{ user?.full_name }}</span>
            </div>

            <!-- Email -->
            <div class="info-row">
                <label>Email</label>
                <input
                    v-if="editing"
                    v-model="localFormData.email"
                    type="email"
                    class="input-field"
                    placeholder="Enter your email"
                />
                <span v-else class="info-value">{{ user?.email }}</span>
            </div>

            <!-- School -->
            <div class="info-row">
                <label>School</label>
                <input
                    v-if="editing"
                    v-model="localFormData.school"
                    type="text"
                    class="input-field"
                    placeholder="Enter your school name"
                />
                <span v-else class="info-value">{{ user?.school || 'Not specified' }}</span>
            </div>

            <!-- Action Buttons -->
            <div v-if="editing" class="card-actions">
                <button @click="cancelEdit" class="cancel-btn">
                    Cancel
                </button>
                <button
                    @click="saveInfo"
                    class="save-btn"
                    :disabled="saving"
                >
                    {{ saving ? 'Saving...' : 'Save Changes' }}
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { ref, watch } from 'vue';

export default {
    name: 'PersonalInfoCard',
    props: {
        user: {
            type: Object,
            default: null
        },
        saving: {
            type: Boolean,
            default: false
        }
    },
    emits: ['save', 'cancel'],
    setup(props, { emit }) {
        const editing = ref(false);
        const localFormData = ref({
            full_name: '',
            email: '',
            school: ''
        });

        // Initialize form data from user prop
        const initFormData = () => {
            localFormData.value = {
                full_name: props.user?.full_name || '',
                email: props.user?.email || '',
                school: props.user?.school || ''
            };
        };

        // Watch for user changes to update form data
        watch(() => props.user, () => {
            if (!editing.value) {
                initFormData();
            }
        }, { immediate: true, deep: true });

        // Watch for saving to become false (save completed)
        watch(() => props.saving, (newVal, oldVal) => {
            if (oldVal === true && newVal === false) {
                editing.value = false;
            }
        });

        const startEditing = () => {
            initFormData();
            editing.value = true;
        };

        const cancelEdit = () => {
            initFormData();
            editing.value = false;
            emit('cancel');
        };

        const saveInfo = () => {
            emit('save', { ...localFormData.value });
        };

        return {
            editing,
            localFormData,
            startEditing,
            cancelEdit,
            saveInfo
        };
    }
};
</script>

<style scoped>
.profile-card {
    background: rgba(30, 30, 30, 0.4);
    border: 1px solid rgba(var(--accent-rgb), 0.6);
    overflow: hidden;
}

.card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px 30px;
    background: var(--bg-secondary);
    border-bottom: 1px solid var(--accent);
}

.card-header h2 {
    margin: 0;
    color: var(--accent);
    font-size: 24px;
}

.edit-btn {
    padding: 8px 20px;
    background: transparent;
    color: var(--accent);
    border: 1px solid var(--accent);
    cursor: pointer;
    transition: all 0.2s;
    font-size: 14px;
}

.edit-btn:hover {
    background: var(--accent);
    transform: translateY(-1px);
    color: var(--bg-primary);
}

.card-content {
    padding: 30px;
    background-color: var(--bg-primary);
}

.info-row {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 25px;
}

.info-row:last-child {
    margin-bottom: 0;
}

.info-row label {
    color: var(--accent);
    font-size: 14px;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 1.2px;
}

.info-value {
    color: var(--text-primary);
    font-size: 18px;
    padding: 12px 0;
}

.input-field {
    width: 100%;
    padding: 12px 16px;
    background: var(--bg-secondary);
    border: 1px solid var(--accent);
    color: var(--text-primary);
    font-size: 16px;
    transition: all 0.2s;
}

.input-field:focus {
    outline: none;
    border-color: var(--accent);
    background: var(--bg-secondary);
}

.input-field::placeholder {
    color: rgba(255, 246, 227, 0.4);
}

.card-actions {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    margin-top: 30px;
    padding-top: 20px;
    border-top: 1px solid var(--accent);
}

.cancel-btn {
    padding: 12px 24px;
    background: transparent;
    color: var(--text-primary);
    border: 1px solid var(--accent);
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.2s;
    font-size: 14px;
}

.cancel-btn:hover {
    background: var(--bg-secondary);
}

.save-btn {
    padding: 12px 24px;
    background: var(--accent);
    color: var(--bg-primary);
    border: none;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.2s;
    font-size: 14px;
    font-weight: 600;
}

.save-btn:hover:not(:disabled) {
    background: var(--text-primary);
    transform: translateY(-2px);
}

.save-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

@media (max-width: 768px) {
    .card-header {
        padding: 15px 20px;
        flex-direction: column;
        gap: 12px;
        align-items: flex-start;
    }

    .card-content {
        padding: 20px;
    }

    .card-actions {
        flex-direction: column;
    }

    .cancel-btn,
    .save-btn {
        width: 100%;
    }
}
</style>
